import { useState } from "react"
import { Head, Link, router, usePage } from "@inertiajs/react"
import { PublicShell } from "@/components/PublicShell"
import { ChevronLeft } from "lucide-react"

type PersonFormProps = {
  id: number | null
  slug: string | null
  name: string
  content: string | null
  birth_date: string | null
  birth_date_precision: string | null
  birth_place: string | null
  death_date: string | null
  death_date_precision: string | null
  death_place: string | null
  death_age: number | null
  meta_title: string | null
  meta_description: string | null
  meta_keywords: string | null
  all_recordings_added: boolean
  in_amh: boolean
  amh_url: string | null
}

type PageProps = { person: PersonFormProps; errors?: Record<string, string> }

const DATE_PRECISIONS = ["day", "month", "day_month", "year", "unknown"]

const inputClass = "w-full px-3 py-2 bg-white dark:bg-stone-900 border border-stone-200 dark:border-stone-700 rounded-lg text-sm text-stone-900 dark:text-white placeholder-stone-400 focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-amber-400 transition-colors"
const selectClass = "w-full px-3 py-2 bg-white dark:bg-stone-900 border border-stone-200 dark:border-stone-700 rounded-lg text-sm text-stone-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-amber-400 transition-colors"
const textareaClass = `${inputClass} resize-y min-h-[120px]`

function Field({ label, error, children }: { label: string; error?: string; children: React.ReactNode }) {
  return (
    <div>
      <label className="block text-sm font-medium text-stone-700 dark:text-stone-300 mb-1.5">{label}</label>
      {children}
      {error && <p className="mt-1 text-xs text-red-600 dark:text-red-400">{error}</p>}
    </div>
  )
}

export default function PersonForm({ person }: { person: PersonFormProps }) {
  const { errors = {} } = usePage<PageProps>().props
  const isNew = person.id === null
  const [form, setForm] = useState({
    name: person.name ?? "",
    content: person.content ?? "",
    birth_date: person.birth_date ?? "",
    birth_date_precision: person.birth_date_precision ?? "",
    birth_place: person.birth_place ?? "",
    death_date: person.death_date ?? "",
    death_date_precision: person.death_date_precision ?? "",
    death_place: person.death_place ?? "",
    death_age: person.death_age !== null ? String(person.death_age) : "",
    meta_title: person.meta_title ?? "",
    meta_description: person.meta_description ?? "",
    meta_keywords: person.meta_keywords ?? "",
    all_recordings_added: person.all_recordings_added ?? false,
    in_amh: person.in_amh ?? false,
    amh_url: person.amh_url ?? "",
  })

  function handleChange(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) {
    const target = e.target as HTMLInputElement
    setForm((prev) => ({
      ...prev,
      [target.name]: target.type === "checkbox" ? target.checked : target.value,
    }))
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    const path = isNew ? "/people" : `/people/${person.slug}`
    if (isNew) {
      router.post(path, { person: form })
    } else {
      router.patch(path, { person: form })
    }
  }

  const title = isNew ? "New person" : `Edit ${person.name}`
  const backPath = isNew ? "/people" : `/people/${person.slug}`

  return (
    <>
      <Head title={title}>
        <meta name="description" content={`${title} — Australian Music Database.`} />
        <meta property="og:title" content={title} />
        <meta property="og:description" content={`${title} — Australian Music Database.`} />
      </Head>

      <PublicShell>
        <div className="mb-6">
          <Link
            href={backPath}
            className="inline-flex items-center gap-1.5 text-sm text-stone-500 dark:text-stone-400 hover:text-amber-600 dark:hover:text-amber-400 transition-colors no-underline mb-4"
          >
            <ChevronLeft className="w-4 h-4" />
            {isNew ? "People" : person.name}
          </Link>
          <h1
            className="text-2xl font-bold text-stone-900 dark:text-white tracking-tight"
            style={{ fontFamily: "'Space Grotesk', system-ui, sans-serif" }}
          >
            {title}
          </h1>
        </div>

        <form onSubmit={handleSubmit} className="max-w-xl space-y-5">
          {Object.keys(errors).length > 0 && (
            <div className="rounded-lg border border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-950 px-4 py-3">
              <p className="text-sm font-medium text-red-700 dark:text-red-400 mb-1">Please fix the following errors:</p>
              <ul className="list-disc list-inside space-y-0.5">
                {Object.entries(errors).map(([field, message]) => (
                  <li key={field} className="text-sm text-red-600 dark:text-red-400">
                    {field.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase())}: {message}
                  </li>
                ))}
              </ul>
            </div>
          )}
          <Field label="Name" error={errors.name}>
            <input name="name" type="text" value={form.name} onChange={handleChange} className={inputClass} />
          </Field>

          <Field label="Bio" error={errors.content}>
            <textarea name="content" value={form.content} onChange={handleChange} className={textareaClass} />
          </Field>

          <div className="grid grid-cols-2 gap-4">
            <Field label="Birth date" error={errors.birth_date}>
              <input name="birth_date" type="date" value={form.birth_date} onChange={handleChange} className={inputClass} />
            </Field>
            <Field label="Birth date precision" error={errors.birth_date_precision}>
              <select name="birth_date_precision" value={form.birth_date_precision} onChange={handleChange} className={selectClass}>
                <option value="">— select —</option>
                {DATE_PRECISIONS.map((p) => <option key={p} value={p}>{p}</option>)}
              </select>
            </Field>
          </div>

          <Field label="Birth place" error={errors.birth_place}>
            <input name="birth_place" type="text" value={form.birth_place} onChange={handleChange} className={inputClass} />
          </Field>

          <div className="grid grid-cols-2 gap-4">
            <Field label="Death date" error={errors.death_date}>
              <input name="death_date" type="date" value={form.death_date} onChange={handleChange} className={inputClass} />
            </Field>
            <Field label="Death date precision" error={errors.death_date_precision}>
              <select name="death_date_precision" value={form.death_date_precision} onChange={handleChange} className={selectClass}>
                <option value="">— select —</option>
                {DATE_PRECISIONS.map((p) => <option key={p} value={p}>{p}</option>)}
              </select>
            </Field>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <Field label="Death place" error={errors.death_place}>
              <input name="death_place" type="text" value={form.death_place} onChange={handleChange} className={inputClass} />
            </Field>
            <Field label="Death age" error={errors.death_age}>
              <input name="death_age" type="number" value={form.death_age} onChange={handleChange} className={inputClass} min={0} max={150} />
            </Field>
          </div>

          <div className="border-t border-stone-100 dark:border-stone-800 pt-5 space-y-4">
            <h2 className="text-sm font-semibold text-stone-700 dark:text-stone-300">SEO & metadata</h2>
            <Field label="Meta title" error={errors.meta_title}>
              <input name="meta_title" type="text" value={form.meta_title} onChange={handleChange} className={inputClass} />
            </Field>
            <Field label="Meta description" error={errors.meta_description}>
              <textarea name="meta_description" value={form.meta_description} onChange={handleChange} className={`${inputClass} resize-y min-h-[80px]`} />
            </Field>
            <Field label="Meta keywords" error={errors.meta_keywords}>
              <input name="meta_keywords" type="text" value={form.meta_keywords} onChange={handleChange} className={inputClass} />
            </Field>
          </div>

          <div className="border-t border-stone-100 dark:border-stone-800 pt-5 space-y-3">
            <h2 className="text-sm font-semibold text-stone-700 dark:text-stone-300">Flags</h2>
            <label className="flex items-center gap-3 cursor-pointer">
              <input name="in_amh" type="checkbox" checked={form.in_amh} onChange={handleChange} className="w-4 h-4 rounded border-stone-300 dark:border-stone-600 text-amber-500 focus:ring-amber-500" />
              <span className="text-sm text-stone-700 dark:text-stone-300">Featured in AMH</span>
            </label>
            <label className="flex items-center gap-3 cursor-pointer">
              <input name="all_recordings_added" type="checkbox" checked={form.all_recordings_added} onChange={handleChange} className="w-4 h-4 rounded border-stone-300 dark:border-stone-600 text-amber-500 focus:ring-amber-500" />
              <span className="text-sm text-stone-700 dark:text-stone-300">All recordings added</span>
            </label>
            <div>
              <label htmlFor="amh_url" className="block text-sm font-medium text-stone-700 dark:text-stone-300 mb-1.5">AMH URL</label>
              <input id="amh_url" name="amh_url" type="url" value={form.amh_url} onChange={handleChange} placeholder="https://australianmusichistory.com/…" className={inputClass} />
              <p className="mt-1 text-xs text-ink-muted">The person's post on australianmusichistory.com, if they have one. Setting it ticks Featured in AMH.</p>
              {errors.amh_url && <p className="mt-1 text-xs text-red-600 dark:text-red-400">{errors.amh_url}</p>}
            </div>
          </div>

          <div className="flex items-center gap-3 pt-2">
            <button
              type="submit"
              className="px-4 py-2 text-sm font-medium bg-amber-500 hover:bg-amber-600 text-white rounded-lg transition-colors"
            >
              {isNew ? "Create person" : "Save changes"}
            </button>
            <Link
              href={backPath}
              className="px-4 py-2 text-sm font-medium text-stone-600 dark:text-stone-400 hover:text-stone-900 dark:hover:text-white transition-colors no-underline"
            >
              Cancel
            </Link>
          </div>
        </form>
      </PublicShell>
    </>
  )
}
